/* eslint-disable camelcase */
/* eslint-disable no-console */
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BundlesType from 'types/BundlesType';
import CreditCardLogos from 'components/CreditCardLogos';
import CustomerInfo from 'types/CustomerInfo';
import { Luci_Shopping_Api_Models_OrderItem, Luci_Shopping_Api_Models_FailureDetails } from 'types/api/v7.1';
import { OrderStatus } from '../../types/Order';
import { PaymentResponse } from '../../types/PaymentResponse';
import PaymentFormInfo from '../../types/PaymentFormInfo';
import TaxStrategyType from '../../types/TaxStrategyType';
import {
  finalizeOrder,
  isReportedApiError,
  payInvoice,
  useGetInvoice,
} from '../../api/ShoppingApi';
import CostCalculationTable from '../../components/CostCalculationTable';
import PaymentIframe from '../../components/PaymentIframe';
import PayPal from '../../components/PaymentProcessors/PayPal';
import ProductAccordion from '../../components/ProductAccordion';
import ProductLineItem from '../../components/ProductLineItem';
import Invoice from '../../types/Invoice';
import InvoiceDetails from '../../types/InvoiceDetails';
import OrderItem from '../../types/OrderItem';
import WebCheckoutCaptureResponsePayPalType from '../../types/response/WebCheckoutCaptureResponsePayPalType';
import GAEvent, { GATransactionEvent } from '../../utilities/analytics';
import { clearStoredPaymentForm } from '../../utilities/paymentFormStorage';
import {
  addFlowBreadcrumb,
  markPaymentPageReached,
  recordPayAttempt,
  trackFlowRedirect,
  trackPaymentFailureWithoutDetails,
} from '../../utilities/paymentFlowTelemetry';
import { isOrderPaid, reportPaymentFailure } from './paymentOutcome';

type Props = {
  invoice: Invoice;
  customerInfo: CustomerInfo;
  invoiceDetails?: InvoiceDetails;
  invoiceId: string;
  paymentFormInfo: PaymentFormInfo;
  taxStrategy: TaxStrategyType;
  paymentType: string;
  setInvoiceDetails: (invoice: Invoice) => void;
  setPaymentFormInfo: (paymentFormInfo: PaymentFormInfo) => void;
};

type PaymentErrorDialog = {
  isDisplayed: boolean;
  title?: string;
  message: string | React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  } | null;
  dismissButtonText?: string;
};

function InvoicePortalPayment({
  invoice,
  customerInfo,
  invoiceDetails,
  invoiceId,
  paymentFormInfo,
  taxStrategy,
  paymentType,
  setInvoiceDetails,
  setPaymentFormInfo,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();

  const [completingPayment, setCompletingPayment] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<
    { error: { messages: string[] } } | undefined
  >();
  const [paymentErrorDialog, setPaymentErrorDialog] =
    useState<PaymentErrorDialog>({
      isDisplayed: false,
      message: '',
      title: undefined,
      action: null,
    });

  const { mutateInvoice } = useGetInvoice(invoiceId);
  const { phone, shippingAddress, billingAddress } = customerInfo;
  const fulfillmentMethod = invoiceDetails?.order?.fulfillmentMethod;
  const toBeShipped = fulfillmentMethod === 'Shipping';
  const orderId = invoiceDetails?.order?.id;

  // Per-tab record that the user actually reached the payment form, so a later
  // bounce back to Billing can be recognised as a bounce rather than a first
  // visit (see utilities/paymentFlowTelemetry).
  useEffect(() => {
    markPaymentPageReached(invoiceId, paymentType);
  }, [invoiceId, paymentType]);

  // Shared context for every redirect this page can trigger.
  const redirectContext = {
    invoiceId,
    fromPage: 'Payment' as const,
    orderId,
    tenantId: invoice.tenantId,
    orderStatus: invoiceDetails?.order?.status,
    hadPaymentFormInfo: true,
    paymentType,
  };

  async function tryFinalizeOrder(latestInvoice?: Invoice): Promise<void> {
    // Re-finalizing a paid order is rejected by the API (422 "Can not change
    // the ShippingAmount after an order has been paid for").
    if (isOrderPaid(latestInvoice)) {
      addFlowBreadcrumb('pay: skip re-finalize, order already paid', { orderId });
      setCompletingPayment(false);
      return;
    }
    addFlowBreadcrumb('pay: re-finalizing after failure', { orderId });
    try {
      await finalizeOrder(
        invoiceId,
        phone || '',
        toBeShipped ? shippingAddress : null,
        billingAddress ?? shippingAddress
      )
        .then((response) => {
          addFlowBreadcrumb('pay: re-finalize response', {
            hasPaymentFormInfo: !!response?.paymentFormInfo,
            orderStatus: response?.order?.status,
          });
          if (response?.paymentFormInfo) {
            setPaymentFormInfo(response.paymentFormInfo);
          }
        })
        .catch((error) => {
          addFlowBreadcrumb('pay: re-finalize failed', undefined, 'error');
          if (!isReportedApiError(error)) {
            Sentry.captureException(error, { level: 'warning' });
          }
        });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setCompletingPayment(false);
    }
  }

  const completeOrder = (callcount = 0): void => {
    if (callcount < 5) {
      if (invoiceDetails?.order?.status?.toLowerCase() !== 'completed') {
        setTimeout(() => {
          completeOrder(callcount + 1);
        }, 2000);
      } else {
        navigate(`/invoice/${invoiceId}/confirmation`);
      }
    }
  };

  const handleCloseErrorDialog = (): void => {
    setPaymentErrorDialog({
      isDisplayed: false,
      message: '',
      title: undefined,
      action: undefined,
      dismissButtonText: undefined,
    });
  };

  const completeCardPayment = (): void => {
    const completedOrderStatusUpdate = {
      ...invoice,
      invoiceDetails: {
        ...invoiceDetails,
        order: {
          ...invoiceDetails?.order,
          status: 'Completed' as OrderStatus,
        },
      },
    };
    if (invoice.tenantId !== undefined) {
      GAEvent('Completed Order', invoice.tenantId);
    }
    if (invoiceDetails?.order) {
      GATransactionEvent(invoiceDetails, 'purchase');
    }
    setInvoiceDetails(completedOrderStatusUpdate);
    completeOrder();
  };

  const onPaymentSubmit = async (
    paymentToken: string,
    responsePayPal?: WebCheckoutCaptureResponsePayPalType
  ): Promise<void> => {
    if (!invoiceId) return;
    setPaymentError(undefined);

    if (paymentType === 'Card') {
      if (!paymentToken) return;

      if (!customerInfo) {
        const to = `/invoice/${invoiceId}/address?error=address,city,state,zipcode`;
        trackFlowRedirect('client_billing_validation', {
          ...redirectContext,
          toPath: to,
          detail: 'customerInfo missing',
        });
        navigate(to);
      }

      let billingErrors = [];
      if (!customerInfo.billingAddress?.line1) billingErrors.push('address');
      if (!customerInfo.billingAddress?.city) billingErrors.push('city');
      if (!customerInfo.billingAddress?.state) billingErrors.push('state');
      if (!customerInfo.billingAddress?.zip) billingErrors.push('zipcode');
      if (billingErrors.length > 0) {
        const to = `/invoice/${invoiceId}/address?error=${billingErrors.join(',')}`;
        // NOTE: execution deliberately continues into payInvoice below today;
        // the redirect is recorded so we can see how often this path fires.
        trackFlowRedirect('client_billing_validation', {
          ...redirectContext,
          toPath: to,
          detail: billingErrors.join(','),
        });
        navigate(to);
      }

      setCompletingPayment(true);
      recordPayAttempt();
      // The token is single-use and the hosted transaction is voided if this
      // attempt fails, so the stored form must not be restored by a reload
      // from here on. A re-finalize after a failure stores the fresh form via
      // setPaymentFormInfo; a success ends on Confirmation.
      clearStoredPaymentForm(invoiceId);
      addFlowBreadcrumb('pay: submitting card token', { orderId });

      let response: PaymentResponse | undefined;
      try {
        response = await payInvoice(
          invoiceId,
          paymentToken,
          customerInfo?.billingAddress ?? customerInfo?.shippingAddress ?? null
        );
      } catch (error) {
        // Network failure ("Load failed" / "Failed to fetch"). The request may
        // still have reached the API, so check the order before reporting.
        addFlowBreadcrumb('pay: request threw', undefined, 'error');
        Sentry.captureException(error);
      }

      let latestInvoice: Invoice | undefined;
      try {
        latestInvoice = await mutateInvoice();
      } catch (error) {
        addFlowBreadcrumb('pay: invoice refetch failed', undefined, 'error');
      }

      addFlowBreadcrumb(
        'pay: response received',
        {
          isSuccess: response?.isSuccess,
          hasFailures: !!response?.failures,
          requestThrew: !response,
        },
        response?.isSuccess ? 'info' : 'warning'
      );

      if (response?.isSuccess) {
        completeCardPayment();
        return;
      }

      if (isOrderPaid(latestInvoice)) {
        // The pay call errored (5xx / empty body / dropped connection) but the
        // charge landed. Previously the user was told to try again after being
        // charged, and the re-finalize then failed with a 422.
        Sentry.captureMessage('Payment recovered: order paid after failed pay response', {
          level: 'warning',
          tags: { 'pay.recovered_after_error': 'true', invoiceId },
        });
        completeCardPayment();
        return;
      }

      if (response?.failures) {
        // fix for an invoice for a store that has a cancelled ProPay account
        const { addressError, cvvError, otherError, zipCodeError } =
          response.failures as Luci_Shopping_Api_Models_FailureDetails;

        if (!addressError && !cvvError && !otherError && !zipCodeError) {
          // Plain decline: the API gives us nothing to show, so the
          // user only sees the card form reload. Track how often.
          trackPaymentFailureWithoutDetails({ invoiceId, orderId });
        }

        billingErrors = [];
        if (addressError) billingErrors.push('address');
        if (zipCodeError) billingErrors.push('zipcode');
        if (billingErrors.length > 0) {
          const to = `/invoice/${invoiceId}/address?error=${billingErrors.join(
            ','
          )}`;
          trackFlowRedirect('avs_address_or_zip_failure', {
            ...redirectContext,
            toPath: to,
            detail: billingErrors.join(','),
          });
          navigate(to);
        }

        const allErrorMessages: string[] = [];
        if (cvvError) allErrorMessages.push(cvvError);
        if (otherError) allErrorMessages.push(otherError);

        if (allErrorMessages.length > 0) {
          setPaymentError({ error: { messages: allErrorMessages } });
          Sentry.captureMessage(
            `Warning: Payment Submit - User Information Issue. ${JSON.stringify(
              allErrorMessages
            )}`,
            'warning'
          );
        }
      } else {
        setPaymentError({
          error: { messages: ['There was a problem. Please try again.'] },
        });
      }

      tryFinalizeOrder(latestInvoice);
    } else if (paymentType === 'PayPal') {
      if (responsePayPal?.isSuccess) {
        const completedOrderStatusUpdate = {
          ...invoice,
          transactionId: responsePayPal.transactionId,
          invoiceDetails: {
            ...invoiceDetails,
            order: {
              ...invoiceDetails?.order,
              status: 'Completed' as OrderStatus,
            },
          },
        };

        if (invoice.tenantId !== undefined) {
          GAEvent('Completed PayPal Order', invoice.tenantId);
        }
        if (invoiceDetails?.order) {
          GATransactionEvent(invoiceDetails, 'purchase');
        }
        if (invoiceDetails) {
          GATransactionEvent(invoiceDetails, 'purchase');
        }
        setInvoiceDetails(completedOrderStatusUpdate);
        completeOrder();
      } else if (responsePayPal?.failures?.descriptiveErrors) {
        setCompletingPayment(false);
        if (responsePayPal.failures.descriptiveErrors.length > 1) {
          setPaymentErrorDialog({
            isDisplayed: true,
            title: 'Payment failed for the following reasons',
            message: (<ul>
              {responsePayPal.failures.descriptiveErrors.map(error => (
                <li key={error.issue}>
                  {error.description}
                </li>
              ))}
            </ul>),
            action: {
              label: 'Back to Payment',
              onClick: () => {
                const to = `/invoice/${invoiceId}/address`;
                trackFlowRedirect('paypal_error_back_to_billing', {
                  ...redirectContext,
                  toPath: to,
                });
                navigate(to);
                setPaymentErrorDialog({
                  isDisplayed: false,
                  message: '',
                  action: null,
                  dismissButtonText: undefined,
                });
              },
            },
            dismissButtonText: 'Dismiss',
          });
        } else {
          setPaymentErrorDialog({
            isDisplayed: true,
            message: responsePayPal.failures.descriptiveErrors[0].description || "There was an issue processing your request. Please try again.",
            action: {
              label: 'Back to Payment',
              onClick: () => {
                const to = `/invoice/${invoiceId}/address`;
                trackFlowRedirect('paypal_error_back_to_billing', {
                  ...redirectContext,
                  toPath: to,
                });
                navigate(to);
                setPaymentErrorDialog({
                  isDisplayed: false,
                  message: '',
                  action: null,
                  dismissButtonText: undefined,
                });
              },
            },
            dismissButtonText: 'Dismiss',
          });
        }
      }
    }
  };

  const onPaymentFailure = (code: string, message?: string): void => {
    setCompletingPayment(true);
    setPaymentError(undefined);

    switch (code) {
      case 'RS_PROPAY_AVS_ERROR':
        setPaymentError({
          error: {
            messages: [
              message ||
                'There was a problem verifying your address information.',
            ],
          },
        });
        setCompletingPayment(false);
        break;
      case 'RS_PROPAY_ERROR':
        setPaymentError({
          error: {
            messages: [
              message ||
                'There was a problem verifying your payment. Please check your card information.',
            ],
          },
        });

        tryFinalizeOrder();
        break;
      default:
        setPaymentError({
          error: {
            messages: [
              message || 'There was a problem processing your transaction.',
            ],
          },
        });
        setCompletingPayment(false);
        break;
    }

    try {
      reportPaymentFailure(code, message, invoiceId);
    } catch (ex) {
      console.error('Sentry error capture failed:', ex);
    }
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('InvoicePortalPayment - Data Analysis:', {
      hasInvoiceDetails: !!invoiceDetails,
      hasOrder: !!invoiceDetails?.order,
      hasOrderItems: !!invoiceDetails?.order?.orderItems,
      orderItemsLength: invoiceDetails?.order?.orderItems?.length || 0,
      invoiceId,
      orderStatus: invoiceDetails?.order?.status,
    });

    console.log('Invoice Structure Check:', {
      invoiceStructureIntact: !!invoice?.invoiceDetails?.order?.orderItems,
      invoiceDetailsMismatch:
        !!invoice?.invoiceDetails?.order?.orderItems &&
        !invoiceDetails?.order?.orderItems,
      invoiceOrderItemsLength:
        invoice?.invoiceDetails?.order?.orderItems?.length || 0,
    });
  }

  return (
    <Grid container spacing={0} className="my-4">
      <>
        <Grid item xs={12}>
          <List
            sx={{ border: `1px solid ${theme.palette.divider}` }}
            disablePadding
          >
            {shippingAddress && toBeShipped && (
              <Grid item xs={12} data-testid="edit-addresses">
                <ListItem className="d-block">
                  <div className="row" style={{ fontSize: '14px' }}>
                    <div className="col-3 col-md-2 align-self-center">
                      Ship To
                    </div>
                    <div className="col-9 col-md-7 align-self-center">
                      {`${shippingAddress.firstName || ''} ${
                        shippingAddress.lastName || ''
                      }`}
                      <br />
                      {shippingAddress.line1 || ''}
                      {shippingAddress.line2 ? ` ${shippingAddress.line2}` : ''}
                      <br />
                      {shippingAddress.city || ''},{' '}
                      {shippingAddress.state || ''} {shippingAddress.zip || ''}
                    </div>
                    <div className="col-12 col-md-3 align-self-center text-end">
                      <Button
                        color="primary"
                        component={Link}
                        to={`/invoice/${invoiceId}/address#Shipping-Information`}
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                </ListItem>
              </Grid>
            )}
            {paymentType !== 'PayPal' && billingAddress && (
              <ListItem className="d-block">
                <div className="row" style={{ fontSize: '14px' }}>
                  <div className="col-3 col-md-2 align-self-center">
                    Billing Address
                  </div>
                  <div className="col-9 col-md-7 align-self-center">
                    {`${billingAddress.firstName || ''} ${
                      billingAddress.lastName || ''
                    }`}
                    <br />
                    {billingAddress.line1 || ''}
                    {billingAddress.line2 ? ` ${billingAddress.line2}` : ''}
                    <br />
                    {billingAddress.city || ''}, {billingAddress.state || ''}{' '}
                    {billingAddress.zip || ''}
                  </div>
                  <div className="col-12 col-md-3 align-self-center text-end">
                    <Button
                      color="primary"
                      component={Link}
                      to={`/invoice/${invoiceId}/address#Billing-Address`}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              </ListItem>
            )}
          </List>
        </Grid>

        {(() => {
          // Using a self-executing function to avoid direct console.log in JSX
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('Attempting to render order items:', {
              renderAttempt: true,
              itemCount: invoiceDetails?.order?.orderItems?.length || 0,
              hasItems: !!invoiceDetails?.order?.orderItems?.length,
              firstItemInfo: (() => {
                // Separate function to avoid nested ternary
                if (invoiceDetails?.order?.orderItems?.[0]) {
                  return `${invoiceDetails.order.orderItems[0].type}-${invoiceDetails.order.orderItems[0].id}`;
                }
                return 'No items';
              })(),
            });
          }
          return null; // Always return null, regardless of environment
        })()}

        <Grid
          container
          spacing={0}
          className="my-2"
          style={{ border: '1px dashed transparent' }}
        >
          <Grid item xs={12}>
            {(() => {
              // Primary case: invoiceDetails has order items
              const invoiceOrderItems = invoiceDetails?.order?.orderItems;
              if (invoiceOrderItems && invoiceOrderItems.length > 0) {
                // We already have the items in a variable
                return invoiceOrderItems.map((item, index) => {
                  // Function to determine which component to render
                  const renderComponent = (): React.ReactNode => {
                    if (item.type === 'Bundle') {
                      return (
                        <ProductAccordion
                          key={`${item.type}-${
                            item.itemNumberRetailerAssigned ??
                            item.id ??
                            'unassigned'
                          }-${item.sku}`}
                          lineItem={item as BundlesType}
                        />
                      );
                    }
                    return (
                      <ProductLineItem
                        id={parseInt(item.itemNumberRetailerAssigned ?? '', 10) ?? undefined}
                        key={`${item.type}-${
                          item.itemNumberRetailerAssigned ??
                          item.id ??
                          'unassigned'
                        }-${item.sku}`}
                        type={Luci_Shopping_Api_Models_OrderItem.type.PRODUCT}
                        image={item.image ?? undefined}
                        description={item?.description || ''}
                        name={item.name || ''}
                        sku={item.sku || ''}
                        quantity={item.quantity ?? 0}
                        isFree={item.isFree || false}
                        isReward={item.isReward || false}
                        salePrice={item.salePrice ?? 0}
                      />
                    );
                  };

                  return (
                    <section
                      key={`${item.type}-${item.id}-${item.sku}-${index}`}
                    >
                      {(() => {
                        if (process.env.NODE_ENV === 'development') {
                          // eslint-disable-next-line no-console
                          console.log(`Rendering item ${index}:`, {
                            id: item.id,
                            type: item.type,
                            name: item.name,
                          });
                        }
                        return null; // Always return null, regardless of environment
                      })()}
                      {renderComponent()}
                    </section>
                  );
                });
              }

              // Fallback case: try to use invoice.invoiceDetails if available
              const fallbackOrderItems =
                invoice?.invoiceDetails?.order?.orderItems;
              if (fallbackOrderItems && fallbackOrderItems.length > 0) {
                if (process.env.NODE_ENV === 'development') {
                  // eslint-disable-next-line no-console
                  console.log('Using fallback invoice data for items');
                }
                return fallbackOrderItems.map((item: OrderItem) => {
                  // Function to determine which component to render
                  const renderFallbackComponent = (): React.ReactNode => {
                    if (item.type === 'Bundle') {
                      return (
                        <ProductAccordion
                          key={`${item.type}-${
                            item.itemNumberRetailerAssigned ??
                            item.id ??
                            'unassigned'
                          }-${item.sku}`}
                          lineItem={item as BundlesType}
                        />
                      );
                    }
                    return (
                      <ProductLineItem
                        id={parseInt(item.itemNumberRetailerAssigned ?? '', 10) ?? undefined}
                        key={`${item.type}-${
                          item.itemNumberRetailerAssigned ??
                          item.id ??
                          'unassigned'
                        }-${item.sku}`}
                        type={item.type as Luci_Shopping_Api_Models_OrderItem.type}
                        image={item.image ?? undefined}
                        description={item?.description || ''}
                        name={item.name || ''}
                        sku={item.sku || ''}
                        quantity={item.quantity ?? 0}
                        isFree={item.isFree || false}
                        isReward={item.isReward || false}
                        salePrice={item.salePrice ?? 0}
                      />
                    );
                  };

                  return renderFallbackComponent();
                });
              }

              // Default case: No items found
              return (
                <Typography variant="body1" color="text.secondary">
                  No order items found to display. This could be a data loading
                  issue.
                </Typography>
              );
            })()}
          </Grid>
        </Grid>

        {invoiceDetails?.order?.orderTotal !== undefined &&
          invoiceDetails?.order?.orderTotal > 0 && (
            <Grid item xs={12}>
              <CostCalculationTable
                order={invoiceDetails?.order}
                estimates={invoiceDetails}
                taxStrategy={taxStrategy}
              />
            </Grid>
          )}
        <Grid item xs={12}>
          <Typography component="h4" variant="h4" className="my-3">
            Payment Information
          </Typography>
        </Grid>
        {invoiceDetails?.order?.orderTotal !== undefined &&
        invoiceDetails?.order?.orderTotal > 0 &&
        invoiceId ? (
          <>
            <Grid item xs={12}>
              {paymentType === 'PayPal' && (
                <PayPal
                  onPaymentSubmit={onPaymentSubmit}
                  customerInfo={customerInfo}
                  invoice={invoice}
                  invoiceId={invoiceId}
                  deliveryMethod={fulfillmentMethod || ''}
                />
              )}
              {paymentType === 'Card' && (
                <>
                  {/* Credit card brands display */}
                  <Typography marginBottom=".5rem">
                    Credit Card Types Accepted
                  </Typography>
                  <CreditCardLogos className="mb-4" />

                  {/* Ensure PaymentIframe is always displayed when payment type is Card */}
                  <PaymentIframe
                    orderId={invoiceDetails?.order?.id || 0}
                    onTokenReceived={onPaymentSubmit}
                    onPaymentFailure={onPaymentFailure}
                    payFormUrl={paymentFormInfo.paymentFormUrl || ''}
                    paymentError={paymentError?.error.messages}
                    paymentProcessing={completingPayment}
                  />
                </>
              )}
            </Grid>
            <Dialog
              open={paymentErrorDialog.isDisplayed}
              onClose={handleCloseErrorDialog}
              aria-describedby="alert-dialog-description"
            >
              {paymentErrorDialog.title && <DialogTitle>{paymentErrorDialog.title}</DialogTitle>}
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {paymentErrorDialog.message}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                {paymentErrorDialog.action ? (
                  <>
                    <Button
                      onClick={handleCloseErrorDialog}
                      autoFocus
                      variant="contained"
                      sx={{ backgroundColor: theme.palette.grey[500] }}
                    >
                      {paymentErrorDialog.dismissButtonText || 'Dismiss'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      autoFocus
                      onClick={paymentErrorDialog.action.onClick}
                    >
                      {paymentErrorDialog.action.label}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleCloseErrorDialog}
                    autoFocus
                    variant="contained"
                    color="primary"
                  >
                    {paymentErrorDialog.dismissButtonText || 'Dismiss'}
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <>
            <Typography component="h5" variant="h4">
              No Payment Necessary
            </Typography>
            <Button>Pay</Button>
          </>
        )}
      </>
    </Grid>
  );
}

export default InvoicePortalPayment;
